import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL } from '../utils/constant';
import { 
  FileSpreadsheet, 
  Download, 
  Calendar, 
  Layers, 
  TrendingUp, 
  TrendingDown, 
  CheckCircle2, 
  X, 
  AlertCircle,
  FileCheck
} from 'lucide-react';

const GstReturnModal = ({ isOpen, onClose }) => {
  const companyData = JSON.parse(localStorage.getItem('company')) || {};
  const companyID = localStorage.getItem('companyID') || companyData?.company?.id || 4;
  const companyName = companyData?.company?.company_name || 'JNM Homoeo Sadan';
  const companyGSTIN = companyData?.company?.gst_number || '19ALGPG2790L1ZP';

  // Selection states
  const [filterType, setFilterType] = useState('all'); // 'all', 'month', 'quarter', 'fy', 'custom'
  const [selectedMonth, setSelectedMonth] = useState('10'); // Default October 2026 (matching active data)
  const [selectedYear, setSelectedYear] = useState('2026');
  const [selectedQuarter, setSelectedQuarter] = useState('3');
  const [selectedFY, setSelectedFY] = useState('2026-2027');
  const [customStartDate, setCustomStartDate] = useState('2026-08-01');
  const [customEndDate, setCustomEndDate] = useState('2026-10-31');

  // Preview & download states
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  // Build query string
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('period', filterType);

    if (filterType === 'month') {
      params.append('month', selectedMonth);
      params.append('year', selectedYear);
    } else if (filterType === 'quarter') {
      params.append('quarter', selectedQuarter);
      params.append('year', selectedYear);
    } else if (filterType === 'fy') {
      params.append('fy', selectedFY);
    } else if (filterType === 'custom') {
      params.append('startDate', customStartDate);
      params.append('endDate', customEndDate);
    }
    return params.toString();
  };

  // Fetch summary preview
  const fetchSummary = async () => {
    try {
      setLoadingSummary(true);
      const q = buildQueryParams();
      const res = await axios.get(`${API_URL}/gst/summary/${companyID}?${q}`);
      if (res.data?.success) {
        setSummaryData(res.data.summary);
      }
    } catch (err) {
      console.error('Error fetching GST summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSummary();
    }
  }, [isOpen, filterType, selectedMonth, selectedYear, selectedQuarter, selectedFY, customStartDate, customEndDate]);

  // Handle Download
  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      const q = buildQueryParams();
      const downloadUrl = `${API_URL}/gst/return-excel/${companyID}?${q}`;

      const response = await axios.get(downloadUrl, {
        responseType: 'blob'
      });

      // Extract filename from header if present, or format default
      let filename = `GST_Return_${companyGSTIN}_${filterType}.xlsx`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        const matches = disposition.match(/filename="?([^"]+)"?/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `GST Return Excel Downloaded`,
        text: filename,
        showConfirmButton: false,
        timer: 4000
      });

    } catch (error) {
      console.error('GST Download error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: error?.response?.data?.message || 'Could not generate GST Excel return. Please try again.',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return `₹ ${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div 
      className="modal fade show d-block" 
      tabIndex="-1" 
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1055 }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '880px' }}>
        <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
          
          {/* Header */}
          <div 
            className="modal-header border-0 text-white p-4"
            style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
            }}
          >
            <div className="d-flex align-items-center">
              <div 
                className="bg-white bg-opacity-20 rounded-3 p-2 me-3 d-flex align-items-center justify-content-center"
                style={{ width: '48px', height: '48px' }}
              >
                <FileSpreadsheet size={28} className="text-white" />
              </div>
              <div>
                <h5 className="modal-title fw-bold text-white mb-0" style={{ fontSize: '1.25rem' }}>
                  GST Return Excel Generator (GSTR-1 & GSTR-3B)
                </h5>
                <p className="text-white text-opacity-80 small mb-0 mt-1">
                  Dynamically compiled from sales & purchase data as per official GST filing format
                </p>
              </div>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
              disabled={downloading}
            />
          </div>

          <div className="modal-body p-4" style={{ backgroundColor: '#f8fafc' }}>
            
            {/* Company Info Badge */}
            <div className="bg-white rounded-3 p-3 border shadow-sm mb-4 d-flex flex-wrap justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <span className="badge bg-primary-subtle text-primary fw-semibold px-3 py-2 me-3 fs-6">
                  {companyName}
                </span>
                <div>
                  <span className="text-muted small me-2">GSTIN:</span>
                  <span className="fw-bold font-monospace text-dark fs-6">{companyGSTIN}</span>
                </div>
              </div>
              <div className="d-flex align-items-center mt-2 mt-sm-0">
                <span className="badge bg-success-subtle text-success d-flex align-items-center px-2 py-1">
                  <CheckCircle2 size={14} className="me-1" /> Multi-Sheet Template Ready
                </span>
              </div>
            </div>

            {/* Period Selector Card */}
            <div className="bg-white rounded-3 p-4 border shadow-sm mb-4">
              <label className="form-label fw-bold text-dark d-flex align-items-center mb-3">
                <Calendar size={18} className="text-primary me-2" />
                Select Return Filing Period:
              </label>

              {/* Quick Select Buttons */}
              <div className="btn-group w-100 mb-3" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${filterType === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setFilterType('all')}
                >
                  All Records
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filterType === 'month' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setFilterType('month')}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filterType === 'quarter' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setFilterType('quarter')}
                >
                  Quarterly
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filterType === 'fy' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setFilterType('fy')}
                >
                  Financial Year
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filterType === 'custom' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setFilterType('custom')}
                >
                  Custom Range
                </button>
              </div>

              {/* Conditional Controls */}
              {filterType === 'month' && (
                <div className="row g-2">
                  <div className="col-md-7">
                    <label className="small text-muted mb-1">Month</label>
                    <select 
                      className="form-select form-select-sm"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April</option>
                      <option value="5">May</option>
                      <option value="6">June</option>
                      <option value="7">July</option>
                      <option value="8">August</option>
                      <option value="9">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                  </div>
                  <div className="col-md-5">
                    <label className="small text-muted mb-1">Year</label>
                    <select 
                      className="form-select form-select-sm"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="2026">2026</option>
                      <option value="2027">2027</option>
                      <option value="2025">2025</option>
                    </select>
                  </div>
                </div>
              )}

              {filterType === 'quarter' && (
                <div className="row g-2">
                  <div className="col-md-7">
                    <label className="small text-muted mb-1">Quarter (Financial Year)</label>
                    <select 
                      className="form-select form-select-sm"
                      value={selectedQuarter}
                      onChange={(e) => setSelectedQuarter(e.target.value)}
                    >
                      <option value="1">Q1 (April - June)</option>
                      <option value="2">Q2 (July - September)</option>
                      <option value="3">Q3 (October - December)</option>
                      <option value="4">Q4 (January - March)</option>
                    </select>
                  </div>
                  <div className="col-md-5">
                    <label className="small text-muted mb-1">Financial Year Base</label>
                    <select 
                      className="form-select form-select-sm"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="2026">2026-2027</option>
                      <option value="2025">2025-2026</option>
                    </select>
                  </div>
                </div>
              )}

              {filterType === 'fy' && (
                <div>
                  <label className="small text-muted mb-1">Financial Year</label>
                  <select 
                    className="form-select form-select-sm"
                    value={selectedFY}
                    onChange={(e) => setSelectedFY(e.target.value)}
                  >
                    <option value="2026-2027">FY 2026-2027 (Apr 2026 - Mar 2027)</option>
                    <option value="2025-2026">FY 2025-2026 (Apr 2025 - Mar 2026)</option>
                  </select>
                </div>
              )}

              {filterType === 'custom' && (
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="small text-muted mb-1">Start Date</label>
                    <input 
                      type="date" 
                      className="form-control form-control-sm"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="small text-muted mb-1">End Date</label>
                    <input 
                      type="date" 
                      className="form-control form-control-sm"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Live Data Summary Cards */}
            <div className="row g-3 mb-4">
              
              {/* Sales Card */}
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 rounded-3" style={{ background: '#f0fdf4', borderLeft: '4px solid #16a34a' }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-success small fw-bold text-uppercase d-flex align-items-center">
                        <TrendingUp size={16} className="me-1" /> Outward Sales
                      </span>
                      <span className="badge bg-success bg-opacity-20 text-success">
                        {summaryData?.outward?.totalBills || 0} Bills
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted small d-block">Taxable Value:</span>
                      <h5 className="fw-bold text-dark mb-0">
                        {loadingSummary ? '...' : formatCurrency(summaryData?.outward?.taxableValue)}
                      </h5>
                    </div>
                    <div className="pt-2 border-top border-success-subtle d-flex justify-content-between text-muted small">
                      <span>Total Output Tax:</span>
                      <span className="fw-bold text-success">
                        {loadingSummary ? '...' : formatCurrency(summaryData?.outward?.totalTax)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between text-muted small mt-1">
                      <span>B2B Invoices:</span>
                      <span className="fw-semibold">{summaryData?.outward?.b2bBills || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchases / ITC Card */}
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 rounded-3" style={{ background: '#eff6ff', borderLeft: '4px solid #2563eb' }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-primary small fw-bold text-uppercase d-flex align-items-center">
                        <TrendingDown size={16} className="me-1" /> Inward ITC (Purchases)
                      </span>
                      <span className="badge bg-primary bg-opacity-20 text-primary">
                        {summaryData?.inward?.totalBills || 0} Invoices
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted small d-block">Taxable Purchases:</span>
                      <h5 className="fw-bold text-dark mb-0">
                        {loadingSummary ? '...' : formatCurrency(summaryData?.inward?.taxableValue)}
                      </h5>
                    </div>
                    <div className="pt-2 border-top border-primary-subtle d-flex justify-content-between text-muted small">
                      <span>Eligible Input Tax Credit:</span>
                      <span className="fw-bold text-primary">
                        {loadingSummary ? '...' : formatCurrency(summaryData?.inward?.totalTax)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between text-muted small mt-1">
                      <span>Gross Purchases:</span>
                      <span className="fw-semibold">{formatCurrency(summaryData?.inward?.invoiceValue)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Position Card */}
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 rounded-3" style={{ background: '#f8fafc', borderLeft: '4px solid #4f46e5' }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-indigo small fw-bold text-uppercase d-flex align-items-center" style={{ color: '#4f46e5' }}>
                        <Layers size={16} className="me-1" /> Net Position
                      </span>
                      <span className={`badge ${summaryData?.netPayable?.total <= 0 ? 'bg-success' : 'bg-warning text-dark'}`}>
                        {summaryData?.netPayable?.total <= 0 ? 'ITC Credit' : 'Tax Payable'}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted small d-block">
                        {summaryData?.netPayable?.total <= 0 ? 'Credit Carried Forward:' : 'Net Tax Payable in Cash:'}
                      </span>
                      <h5 className={`fw-bold mb-0 ${summaryData?.netPayable?.total <= 0 ? 'text-success' : 'text-danger'}`}>
                        {loadingSummary ? '...' : formatCurrency(Math.abs(summaryData?.netPayable?.total || 0))}
                      </h5>
                    </div>
                    <div className="pt-2 border-top d-flex justify-content-between text-muted small">
                      <span>IGST Balance:</span>
                      <span className="fw-semibold">{formatCurrency(summaryData?.netPayable?.igst)}</span>
                    </div>
                    <div className="d-flex justify-content-between text-muted small mt-1">
                      <span>CGST / SGST Net:</span>
                      <span className="fw-semibold">{formatCurrency((summaryData?.netPayable?.cgst || 0) + (summaryData?.netPayable?.sgst || 0))}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Sheets Included Info */}
            <div className="p-3 bg-white rounded-3 border shadow-sm">
              <span className="small fw-bold text-dark d-block mb-2 d-flex align-items-center">
                <FileCheck size={16} className="text-success me-1" />
                Worksheet Structure in Downloaded Excel (.xlsx):
              </span>
              <div className="d-flex flex-wrap gap-2">
                <span className="badge bg-light text-dark border">
                  1. GSTR-3B Summary Table
                </span>
                <span className="badge bg-light text-dark border">
                  2. b2b (Registered Invoices)
                </span>
                <span className="badge bg-light text-dark border">
                  3. b2cs (Retail / Small Sales)
                </span>
                <span className="badge bg-light text-dark border">
                  4. b2cl (Large Inter-State)
                </span>
                <span className="badge bg-light text-dark border">
                  5. hsn (HSN Summary Table)
                </span>
                <span className="badge bg-light text-dark border">
                  6. docs (Document Ranges)
                </span>
                <span className="badge bg-light text-dark border">
                  7. Sales Register (Itemized)
                </span>
                <span className="badge bg-light text-dark border">
                  8. Purchase Register (ITC Reconciliation)
                </span>
              </div>
            </div>

          </div>

          {/* Modal Footer */}
          <div className="modal-footer bg-white border-top p-3 d-flex justify-content-between">
            <button 
              type="button" 
              className="btn btn-outline-secondary px-4 rounded-3" 
              onClick={onClose}
              disabled={downloading}
            >
              Close
            </button>
            <button 
              type="button" 
              className="btn btn-success d-flex align-items-center px-4 py-2 rounded-3 fw-bold shadow-sm"
              onClick={handleDownloadExcel}
              disabled={downloading || loadingSummary}
              style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', border: 'none' }}
            >
              {downloading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Generating Excel Workbook...
                </>
              ) : (
                <>
                  <Download size={18} className="me-2" />
                  Download GST Return Excel (.xlsx)
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default GstReturnModal;
